import { useNavigate } from 'react-router-dom';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { SHOPIFY_COPY } from '@lib/services/shopify/connectionError';
import { useShopifyConnectionStore } from '@stores/shopifyConnectionStore';

/**
 * Infra 1.5 #23 —— 未连接时的三处兜底：全局弹窗、首页提示条、就地空态。
 *
 * <p><b>只有这三处。</b>PRD 明说不做逐位置空态改造 —— 依赖 Shopify 的位置有三十多处，逐个改会满屏
 * 都是同一句话。所以：断开时弹一次全局弹窗 ＋ 首页留一条提示条，其余位置等商家点到、接口报错时，
 * 由 `api.ts` 的拦截器统一命中 `isShopifyDisconnected` 并广播，页面就地渲染 {@link ShopifyFallbackEmptyState}。
 * <b>各页面不自己判连接状态，也不写自己的空态。</b>
 *
 * <p><b>先后关系按 connect demo</b>（PRD 文字里没有这一条）：断开瞬间只弹弹窗；
 * <b>关掉弹窗之后</b>提示条才出现，然后一直留着，重连成功才消失。所以两个组件读的是同一个
 * `dialogDismissed`，一个 `!dismissed` 一个 `dismissed`，不会同时出现。
 *
 * <p>文案全部取自 `SHOPIFY_COPY`（全站唯一一份，英文原字串）。弹窗视觉按 Figma 24700:6707：
 * 橙色警示图标、右上角关闭 X、Got It（左）/ Reconnect（右）分列。
 */

const RECONNECT_TO = '/platform/settings#integrations';

/** 全局弹窗。挂在 DashboardLayout 上，不分页面。 */
export function ShopifyDisconnectedModal() {
  const navigate = useNavigate();
  const connected = useShopifyConnectionStore(s => s.connected);
  const dismissed = useShopifyConnectionStore(s => s.dialogDismissed);
  const reason = useShopifyConnectionStore(s => s.disconnectReason);
  const dismissDialog = useShopifyConnectionStore(s => s.dismissDialog);

  // connected === null 是「还不知道」，不是未连接 —— 冷启动瞬间不能闪弹窗。
  // reason !== 'interrupted' 时也不弹：商家自己点的 Disconnect 不该收到「连接已中断」的解释，
  // 从没连过的商家（[Infra 1.5 #24] 的 neverConnected）更不该 —— 没有中断过的东西谈不上中断。
  if (connected !== false || dismissed || reason !== 'interrupted') return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={SHOPIFY_COPY.dialogTitle}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4"
    >
      <div className="relative w-full max-w-[600px] rounded-2xl bg-white p-8 shadow-xl">
        <button
          type="button"
          aria-label="Close"
          onClick={dismissDialog}
          className="absolute right-5 top-5 rounded-full p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-4">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#ff8d28]/12">
            <ExclamationTriangleIcon className="h-8 w-8 text-[#ff8d28]" />
          </span>
          <h2 className="text-2xl font-bold text-[#101828]">{SHOPIFY_COPY.dialogTitle}</h2>
        </div>

        <p className="mt-6 text-base leading-7 text-[#747c80]">{SHOPIFY_COPY.dialogBody}</p>

        <div className="mt-8 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={dismissDialog}
            className="inline-flex h-11 items-center rounded-lg border border-[#e2e8f0] px-5 text-sm font-semibold text-[#2c3437] hover:bg-gray-50"
          >
            {SHOPIFY_COPY.dialogDismiss}
          </button>
          <button
            type="button"
            onClick={() => {
              dismissDialog();
              navigate(RECONNECT_TO);
            }}
            className="inline-flex h-11 items-center rounded-lg bg-[#6b33ff] px-5 text-sm font-semibold text-white hover:opacity-90"
          >
            {SHOPIFY_COPY.dialogAction}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * 首页提示条。<b>只在首页放这一条</b> —— 其他页面不放常驻横幅，否则同一句话会在整个产品里重复。
 */
export function ShopifyDisconnectedBanner() {
  const navigate = useNavigate();
  const connected = useShopifyConnectionStore(s => s.connected);
  const dismissed = useShopifyConnectionStore(s => s.dialogDismissed);
  const reason = useShopifyConnectionStore(s => s.disconnectReason);

  // 弹窗还开着的时候不出提示条 —— 见文件头的先后关系。
  // 商家自己点的 Disconnect 不弹窗，所以那条路径下提示条直接出，不用等关闭动作。
  if (connected !== false) return null;
  if (reason === 'interrupted' && !dismissed) return null;
  // [Infra 1.5 #24] 从没连过的商家（只装挂件 / 只连 WordPress）不出这条 —— 它的动作叫
  // Reconnect，而他没有可以「重新」连的东西。这正是后端把 NEVER_CONNECTED 从
  // DISCONNECTED 里拆出来的理由：别把这些人推进一条会清库的流程。
  if (reason === 'neverConnected') return null;

  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-[#eb001b]/25 bg-[#eb001b]/5 px-4 py-3">
      <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-[#eb001b]" />
      <span className="text-sm font-semibold text-[#2c3437]">{SHOPIFY_COPY.bannerTitle}</span>
      <button
        type="button"
        onClick={() => navigate(RECONNECT_TO)}
        className="ml-auto inline-flex h-8 items-center rounded-lg border border-[#6b33ff] bg-white px-3 text-xs font-semibold text-[#6b33ff] hover:bg-[#f6f3ff]"
      >
        {SHOPIFY_COPY.bannerAction}
      </button>
    </div>
  );
}

/**
 * 就地兜底空态。
 *
 * <p><b>由错误码触发，不由页面各自判断</b>：调用方在它已有的失败分支里渲染这一个组件即可，
 * 不要再写「if (!connected) ...」——连接状态的判定只在 `connectionError.ts` 那一处。
 */
export function ShopifyFallbackEmptyState({ className = '' }: { className?: string }) {
  const navigate = useNavigate();
  return (
    <div
      data-testid="shopify-fallback-empty"
      className={`rounded-xl border border-[#e2e8f0] bg-white px-5 py-8 text-center ${className}`}
    >
      <p className="text-sm font-semibold text-[#2c3437]">{SHOPIFY_COPY.emptyState}</p>
      <button
        type="button"
        onClick={() => navigate(RECONNECT_TO)}
        className="mt-3 inline-flex h-9 items-center rounded-lg border border-[#6b33ff] px-3.5 text-xs font-semibold text-[#6b33ff] hover:bg-[#f6f3ff]"
      >
        {SHOPIFY_COPY.emptyStateAction}
      </button>
    </div>
  );
}

/**
 * Infra 1.5 #22 / #24 —— 重连回跳后的<b>首次同步全屏 loading</b>（Figma「23 重连·回首页初始化」）。
 *
 * <p>挂在 DashboardLayout 上，读单一数据源的 `syncing`，而 `syncing` 现在只由后端真实返回的
 * `SYNCING` 状态播种（`ShopifyStoreConnectionRow`）。
 *
 * <p><b>此前这里有一个 2.5 秒的假计时器</b>，配合那个标着 <code>{'{'}Simulated{'}'}</code> 的
 * 授权弹窗演完整条流程。两个都删了：计时器一旦遇上<b>真</b>的 SYNCING，会在同步还没结束时
 * 就把遮罩撤掉，而那正是这层遮罩存在的唯一理由。
 *
 * <p><b>所以它今天是休眠的</b> —— 后端还没有任何代码写 `SYNCING`，要等「重连即初始化」。
 * 那条流程会同时带来开始与完成两个信号，遮罩的生命周期届时归它，不归这里的定时器。
 */
export function ShopifySyncingOverlay() {
  const syncing = useShopifyConnectionStore(s => s.syncing);

  if (!syncing) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Syncing your store data"
      className="fixed inset-0 z-[70] flex flex-col items-center justify-center gap-4 bg-white"
    >
      <span className="h-10 w-10 animate-spin rounded-full border-[3px] border-[#6b33ff]/25 border-t-[#6b33ff]" />
      <p className="text-base font-semibold text-[#2c3437]">Syncing your store data...</p>
    </div>
  );
}
